import { useRef } from 'react'
import { IAccount, IProduct } from 'src/types'
import { DescriptionPreview, MediaList, ProductImage, QuillHtmlParser } from 'src/components'
import { useIsTablet, useNavigationAvailability } from 'src/hooks'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Divider, Typography } from '@mui/material'
import {
  FAIRTRADE_FONT_STACK,
  FAIRTRADE_TITLE_FONT_STACK,
  FAIRTRADE_SKY_BLUE,
} from 'src/templates/fairtrade/constants'
import ArrowIconLeft from 'src/assets/icons/arrow.icon-left'
import ArrowIcon from 'src/assets/icons/arrow.icon'
import { JOURNEY_PAGE_TEMPLATE_ID } from 'src/templates/constants'
import { PageWrapper } from '../fairtrade.styled'
import { FairtradeFonts } from '../fairtrade-fonts'
import { useLocation } from 'react-router-dom'
import { PATHS } from 'src/constants'
import { stripProductHomeSegment } from 'src/utils/route.utils'
import { FairtradeImpactBubbles } from 'src/templates/fairtrade/components/impact-bubbles'

interface IProps {
  product: IProduct
  account: IAccount
}

export const FairtradeProductTemplate = ({ product, account }: IProps) => {
  const [t, i18n] = useTranslation()
  const isTablet = useIsTablet()
  const location = useLocation()
  const baseProductPath = stripProductHomeSegment(location.pathname)

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scrollLeft = () => {
    if (scrollContainerRef) {
      scrollContainerRef.current.scrollLeft += -130
    }
  }
  const scrollRight = () => {
    if (scrollContainerRef) {
      scrollContainerRef.current.scrollLeft += 130
    }
  }

  const {
    hasJourney,
    hasImpacts,
    impactsToShow = [],
    paths,
    isLoading,
  } = useNavigationAvailability({
    product,
    basePath: baseProductPath,
    language: i18n.language,
  })
  const accountPath = paths.account

  const fairtradeNavColor = account?.alternative_color || '#DBFF4C'
  const productLabel = t('journey-navigation-product-button')
  const journeyLabel = t('journey-navigation-journey-button')
  const impactsLabel = t('journey-navigation-impacts-button')
  const accountLabel = t('journey-navigation-account-button')

  const nextRoute =
    (hasJourney && paths.journey) || (hasImpacts && paths.impacts) || accountPath || '/'

  const productSimpleId = product.simple_id

  const nextState = hasJourney
    ? {
        from: paths.product,
        to: paths.journey ?? `${baseProductPath}/${PATHS.JOURNEY}`,
        previousLabel: productLabel,
      }
    : {
        from: paths.product,
        productId: productSimpleId,
        previousLabel: productLabel,
      }

  const aboutAccountLabel = account?.account_holder
    ? `${accountLabel} ${account.account_holder}`
    : 'About this account'

  const nextLabel = isLoading
    ? ''
    : hasJourney
      ? journeyLabel
      : hasImpacts
        ? impactsLabel
        : aboutAccountLabel

  return (
    <PageWrapper id="index__templates-fairtrade-product__pagewrapper__l89">
      <FairtradeFonts />
      {/* <PageTitle
        id="index__templates-fairtrade-product__pagetitle__l91"
        sx={{
          marginX: {
            xs: '24px',
            md: '8px',
          },
        }}
      >
        {t('journey-product-header-title')}
      </PageTitle> */}

      <ProductImage
        name={product?.name}
        image={product?.image}
        media={product?.mainMedia}
        titleFontFamily={FAIRTRADE_FONT_STACK}
        titleFontSize="24px"
        titleFontWeight={600}
        titleBackgroundColor="#FFFFFF"
        titleColor="#000000"
        titlePlacement="top"
        nextPageOverride={{
          to: nextRoute,
          state: nextState,
          label: nextLabel,
          variant: JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE,
          backgroundColor: fairtradeNavColor,
          textColor: '#000000',
        }}
        menuOverride={{
          variant: JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE,
          backgroundColor: fairtradeNavColor,
          textColor: '#000000',
          topOffset: 0,
        }}
      />

      {account?.show_unsgd_home_page && product.un_sdg && product.un_sdg.length > 0 && (
        <Box
          id="index__templates-fairtrade-product__sdg-row"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
            width: '100%',
            px: { xs: '16px', md: '8px' },
            py: '16px',
          }}
        >
          {product.un_sdg.map((item) => (
            <Box
              key={item.id}
              component="img"
              src={item.image_medium || item.image}
              alt={item.name}
              sx={{ width: 40, height: 40, borderRadius: '6px' }}
            />
          ))}
        </Box>
      )}

      <Box
        id="index__templates-fairtrade-product__avatar-title-row"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '12px',
          px: { xs: '16px', md: '8px' },
          mb: '20px',
        }}
      >
        {(account?.image_small || account?.image) && (
          <Avatar
            src={account?.image_small || account?.image}
            alt={account?.account_holder}
            sx={{ width: 64, height: 64, flexShrink: 0 }}
          />
        )}
        {!account?.hide_product_name && product?.name && (
          <Typography
            id="index__templates-fairtrade-product__title"
            variant="h2"
            sx={{
              fontFamily: FAIRTRADE_TITLE_FONT_STACK,
              fontWeight: 700,
              fontSize: { xs: '24px', md: '28px' },
              color: '#111111',
              lineHeight: 1.2,
            }}
          >
            {product.name}
          </Typography>
        )}
      </Box>

      <Box
        id="index__templates-fairtrade-product__box__l139"
        sx={{
          backgroundColor: '#f8f6f4',
          borderRadius: '24px',
          // paddingLeft: '6px',
          // paddingRight: '6px',
          // [theme.breakpoints.up('md')]: {
          //   paddingLeft: '0px',
          //   paddingRight: '0px',
          // },
          padding: {
            xs: '20px',
            md: '32px',
          },
          mx: {
            xs: '6px',
            md: '0px',
          },
          boxShadow: '0px 18px 45px rgba(23, 34, 49, 0.08)',
          border: '1px solid rgba(23, 34, 49, 0.05)',
          mb: '36px',
        }}
      >
        {/* <BottomCardTitle
          hideAccountName={account?.hide_name}
          twitterTitle={product?.name}
          title={account?.account_holder}
          image={account?.image_small || account?.image}
          accountId={product?.account_simple_id}
          avatarSx={{ borderRadius: '50%' }}
        /> */}

        {product.home_stats && product.home_stats.trim() && product.home_stats !== '<p><br></p>' ? (
          <Box
            id="index__templates-fairtrade-product__home-stats__l167"
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: { xs: '16px', md: '24px' },
              marginBottom: '20px',
              border: '1px solid rgba(23, 34, 49, 0.08)',
              fontFamily: FAIRTRADE_FONT_STACK,
            }}
          >
            <QuillHtmlParser html={product.home_stats} fontSize="22px" />
          </Box>
        ) : null}

        {product.description && product.description !== '<p><br></p>' ? (
          <>
            <DescriptionPreview
              sx={{ marginBottom: '12px', marginTop: '10px' }}
              contentKey={`product-description`}
              toggleColor={FAIRTRADE_SKY_BLUE}
              collapsedHeight={280}
            >
              <QuillHtmlParser html={product.description} fontSize="20px" />
            </DescriptionPreview>

            <Divider
              id="index__templates-fairtrade-product__divider__l166"
              sx={{ borderColor: 'rgba(0,0,0,0.08);' }}
            />
          </>
        ) : null}

        {product?.showMedia?.length > 0 && (
          <>
            <MediaList media={product?.showMedia || []} />
            {/* <Divider sx={{ borderColor: 'rgba(0,0,0,0.08);' }} /> */}
          </>
        )}
      </Box>

      <Box
        id="index__templates-fairtrade-product__box__l178"
        sx={{
          display: 'flex',
          gap: '25px',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          id="index__templates-fairtrade-product__box__l186"
          onClick={scrollLeft}
          sx={{
            cursor: 'pointer',
            display: isTablet || impactsToShow.length < 4 ? 'none' : '',
          }}
        >
          <ArrowIconLeft />
        </Box>

        {impactsToShow.length > 0 && (
          <FairtradeImpactBubbles
            items={impactsToShow}
            ref={scrollContainerRef}
            withArrows={!(isTablet || impactsToShow.length < 4)}
            primaryColor={account?.primary_color}
            walletAddress={account?.wallet_address}
            showWalletAddress={account?.show_wallet_address}
            showBcInfo={account?.show_bc_info}
            accountImage={account?.image_small || account?.image}
            isImpactsPage={hasImpacts}
            disabled={!hasImpacts}
            templateId={JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE}
          />
        )}
        <Box
          id="index__templates-fairtrade-product__box__l211"
          onClick={scrollRight}
          sx={{
            cursor: 'pointer',
            display: isTablet || impactsToShow.length < 4 ? 'none' : '',
          }}
        >
          <ArrowIcon />
        </Box>
      </Box>
    </PageWrapper>
  )
}
